import { useState, useEffect } from 'react';
import { Trash2, Search, Calendar, User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { operationLogService } from '@/services/storage';
import type { OperationLog } from '@/types';
import { toast } from 'sonner';

const moduleColors: Record<string, string> = {
  '产品管理': 'bg-blue-100 text-blue-800',
  '分类管理': 'bg-purple-100 text-purple-800',
  '参数管理': 'bg-green-100 text-green-800',
  '询单管理': 'bg-yellow-100 text-yellow-800',
  '新闻管理': 'bg-orange-100 text-orange-800',
  '荣誉资质': 'bg-pink-100 text-pink-800',
  '招聘职位': 'bg-indigo-100 text-indigo-800',
  '页面内容': 'bg-cyan-100 text-cyan-800',
  '系统配置': 'bg-gray-100 text-gray-800',
  '附件管理': 'bg-teal-100 text-teal-800',
  '邮件': 'bg-red-100 text-red-800',
};

export default function AdminOperationLogs() {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLogs(await operationLogService.getAll());
  };

  const handleClear = async () => {
    if (confirm('确定要清空所有操作日志吗？此操作不可恢复。')) {
      operationLogService.clear();
      loadLogs();
      toast.success('日志已清空');
    }
  };

  const modules = Array.from(new Set(logs.map(log => log.module)));

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = filterModule ? log.module === filterModule : true;
    return matchesSearch && matchesModule;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">操作日志</h1>
        <Button variant="destructive" onClick={handleClear}>
          <Trash2 className="mr-2 h-5 w-5" />
          清空日志
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="搜索操作、详情、用户..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <select
                className="w-full border rounded-md px-3 py-2"
                value={filterModule}
                onChange={(e) => setFilterModule(e.target.value)}
              >
                <option value="">全部模块</option>
                {modules.map(module => (
                  <option key={module} value={module}>{module}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>时间</TableHead>
                <TableHead>用户</TableHead>
                <TableHead>模块</TableHead>
                <TableHead>操作</TableHead>
                <TableHead>详情</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(log.timestamp).toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1 text-gray-400" />
                      {log.user}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={moduleColors[log.module] || 'bg-gray-100 text-gray-800'}>
                      {log.module}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{log.action}</TableCell>
                  <TableCell>
                    <div className="flex items-start">
                      <FileText className="h-4 w-4 mr-1 text-gray-400 mt-0.5" />
                      <span className="text-sm text-gray-600">{log.details}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无操作日志
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-4 text-sm text-gray-500">
        共 {filteredLogs.length} 条记录
      </div>
    </div>
  );
}
